import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Icon,
  LockedFeatureOverlay,
  QuoteTrend,
  Screen,
  StatePicker,
  Text,
  WeatherGlyph,
} from '~/atomic';
import { cn } from '~/utils/cn';
import {
  QUOTE_PRODUCT_COLOR,
  QUOTE_PRODUCT_LABEL,
  QUOTE_PRODUCT_SYMBOL,
  formatQuotePrice,
  relativeFromNow,
  ufName,
  unitFullLabel,
  unitSuffix,
  weatherCityForUf,
} from '~/utils/quotes';
import type { QuoteItem, Weather } from '~/store/quotes/interfaces';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  selectQuotesCoords,
  selectQuotesSettings,
  selectQuotesUf,
  selectVisibleQuoteItems,
  selectWeather,
  selectWeatherLoading,
} from '~/store/quotes/selectors';
import { setLocation, setManualUf } from '~/store/quotes/slices';
import { fetchQuotes, fetchWeather } from '~/store/quotes/thunks';
import { latestMarketIndexesRequest } from '~/services';
import type { propsApiMarketIndexRate } from '~/services';

const fmt = (v: number | null | undefined, suffix = '') =>
  v == null ? '—' : `${v}${suffix}`;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 flex flex-col items-center">
      <Text className="text-captionMD color-intense-04">{label}</Text>
      <Text className="mt-quark text-paragraphSM color-intense-01">{value}</Text>
    </div>
  );
}

function StatDivider() {
  return <div className="w-px h-[26px] self-center bg-intense-01/15" />;
}

function WeatherCard({
  weather,
  loading,
}: {
  weather: Weather | null;
  loading: boolean;
}) {
  const gradient = {
    background: 'linear-gradient(135deg, #0F3823 0%, #1D4733 50%, #3F7D5F 100%)',
  };

  if (!weather) {
    return (
      <div
        className="rounded-[28px] overflow-hidden shadow-level2 h-[132px] flex items-center px-medium"
        style={gradient}
      >
        <Text className="text-bodyMD color-intense-03">
          {loading ? 'Buscando o clima da sua região…' : 'Clima indisponível no momento'}
        </Text>
      </div>
    );
  }

  const today = weather.forecast[0];

  return (
    <div className="rounded-[28px] overflow-hidden shadow-level2 relative" style={gradient}>
      <div
        className="absolute -top-[60px] -right-[50px] w-[220px] h-[220px] rounded-full pointer-events-none"
        style={{ backgroundColor: 'rgba(168, 213, 185, 0.22)' }}
      />
      <div
        className="absolute top-[20px] right-[40px] w-[110px] h-[110px] rounded-full pointer-events-none"
        style={{ backgroundColor: 'rgba(168, 213, 185, 0.12)' }}
      />
      <div
        className="absolute -bottom-[80px] -left-[40px] w-[200px] h-[200px] rounded-full pointer-events-none"
        style={{ backgroundColor: 'rgba(15, 56, 35, 0.45)' }}
      />

      <div className="relative px-medium pt-medium pb-small">
        <div className="flex items-center gap-quark">
          <Icon name="icon-pin-location" size={13} color="#C5DBCB" rounded={false} />
          <Text className="text-captionLG color-intense-03" numberOfLines={1}>
            {weather.city ?? 'Sua região'}
            {weather.uf ? `, ${weather.uf}` : ''}
          </Text>
        </div>

        <div className="mt-small flex items-center justify-between">
          <div className="flex-1 pr-small">
            <Text className="text-display1 color-intense-01">
              {weather.temp != null ? `${Math.round(weather.temp)}°` : '—'}
            </Text>
            <Text className="text-bodyMD color-intense-01" numberOfLines={1}>
              {weather.description ?? '—'}
            </Text>
            {weather.feelsLike != null ? (
              <Text className="text-captionMD color-intense-04">
                {`Sensação ${Math.round(weather.feelsLike)}°`}
              </Text>
            ) : null}
          </div>
          <div
            className="w-[96px] h-[96px] flex items-center justify-center rounded-full"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
          >
            <WeatherGlyph slug={weather.conditionSlug} size={68} color="#FFFFFF" />
          </div>
        </div>

        <div className="mt-small flex border-t border-intense-01/15 pt-small">
          <Stat label="Umidade" value={fmt(weather.humidity, '%')} />
          <StatDivider />
          <Stat label="Vento" value={weather.windSpeedy ?? '—'} />
          <StatDivider />
          <Stat label="Chuva" value={today ? fmt(today.precipitation, ' mm') : '—'} />
          <StatDivider />
          <Stat label="Evapotr." value={today ? fmt(today.et0, ' mm') : '—'} />
        </div>

        {weather.forecast.length > 0 ? (
          <LockedFeatureOverlay
            feature="weather_forecast"
            title="Previsão estendida"
            description="Acompanhe os próximos dias com máxima, mínima e chance de chuva. Disponível nos planos pagos."
            className="mt-small h-[240px]"
          >
          <div className="border-t border-intense-01/15 pt-small">
            <div className="mb-smaller flex items-center justify-between">
              <Text className="text-captionLG color-intense-01">
                Próximos {weather.forecast.length} dias
              </Text>
              <Text className="text-captionMD color-intense-04">máx · mín · chuva</Text>
            </div>
            <div className="flex gap-[6px] overflow-x-auto no-scrollbar pb-quark">
              {weather.forecast.map((d, i) => (
                <div
                  key={d.date}
                  className={cn(
                    'flex flex-col items-center py-quark px-quark rounded-[14px] w-[54px] shrink-0',
                    i === 0 && 'border border-intense-01/25',
                  )}
                  style={i === 0 ? { backgroundColor: 'rgba(255, 255, 255, 0.10)' } : undefined}
                >
                  <Text className="text-captionMD color-intense-03">
                    {i === 0 ? 'Hoje' : d.weekday}
                  </Text>
                  <div className="my-quark">
                    <WeatherGlyph slug={d.conditionSlug} size={24} color="#FFFFFF" />
                  </div>
                  <Text className="text-captionLG color-intense-01">
                    {`${Math.round(d.max)}°`}
                  </Text>
                  <Text className="text-captionMD color-intense-04">
                    {`${Math.round(d.min)}°`}
                  </Text>
                  <div className="mt-quark flex items-center gap-quark">
                    <span className="w-[4px] h-[4px] rounded-full bg-info-03" />
                    <Text className="text-captionMD color-intense-03">
                      {fmt(d.rainProbability, '%')}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </LockedFeatureOverlay>
        ) : null}
      </div>
    </div>
  );
}

function QuoteRow({
  item,
  onClick,
}: {
  item: QuoteItem;
  onClick: () => void;
}) {
  const color = QUOTE_PRODUCT_COLOR[item.product];
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-[18px] bg-intense-01 border border-intense-03 overflow-hidden flex items-stretch shadow-level1 hover:shadow-level2 transition-shadow"
    >
      <div style={{ width: 4, backgroundColor: color }} />
      <div className="flex-1 flex items-center justify-between px-small py-smaller">
        <div className="flex-1 pr-small min-w-0">
          <div className="flex items-center gap-nano">
            <Text className="text-paragraphSM color-complement-01">
              {QUOTE_PRODUCT_LABEL[item.product]}
            </Text>
            <Text className="text-captionMD color-intense-04">
              {QUOTE_PRODUCT_SYMBOL[item.product]}
            </Text>
          </div>
          <Text
            className="mt-nano text-captionMD color-intense-06"
            numberOfLines={1}
          >
            {`${item.praca ?? ufName(item.uf)} · ${unitFullLabel(item.unit)}`}
          </Text>
        </div>
        <div className="flex flex-col items-end">
          <Text className="text-paragraphLG color-complement-01">
            {formatQuotePrice(item.price, item.unit)}
          </Text>
          <div className="mt-nano flex items-center gap-quark">
            <Text className="text-captionMD color-intense-05">
              {unitSuffix(item.unit) || unitFullLabel(item.unit)}
            </Text>
            <QuoteTrend pct={item.change?.pct ?? null} compact />
          </div>
        </div>
      </div>
    </button>
  );
}

export default function MarketOverviewView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const items = useAppSelector(selectVisibleQuoteItems);
  const uf = useAppSelector(selectQuotesUf);
  const weather = useAppSelector(selectWeather);
  const coords = useAppSelector(selectQuotesCoords);
  const { weatherCity, useCurrentLocation } = useAppSelector(
    selectQuotesSettings,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [marketIndexes, setMarketIndexes] = useState<propsApiMarketIndexRate[]>([]);
  const [marketIndexesLoading, setMarketIndexesLoading] = useState(true);
  const [marketIndexesError, setMarketIndexesError] = useState(false);

  useEffect(() => {
    dispatch(fetchQuotes(uf));
  }, [dispatch, uf]);

  useEffect(() => {
    let active = true;
    setMarketIndexesLoading(true);
    setMarketIndexesError(false);
    latestMarketIndexesRequest()
      .then((response) => {
        if (active) setMarketIndexes(response.data);
      })
      .catch(() => {
        if (active) {
          setMarketIndexes([]);
          setMarketIndexesError(true);
        }
      })
      .finally(() => {
        if (active) setMarketIndexesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (weatherCity) {
      dispatch(fetchWeather({ city: weatherCity }));
      return;
    }
    if (coords) {
      dispatch(fetchWeather({ lat: coords.lat, lon: coords.lon }));
      return;
    }
    if (
      useCurrentLocation &&
      typeof navigator !== 'undefined' &&
      navigator.geolocation
    ) {
      let settled = false;
      const fallbackTimer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        const city = weatherCityForUf(uf);
        if (city) dispatch(fetchWeather({ city }));
      }, 8000);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(fallbackTimer);
          const c = { lat: pos.coords.latitude, lon: pos.coords.longitude };
          const result = await dispatch(fetchWeather(c)).unwrap();
          dispatch(setLocation({ uf: result?.uf ?? uf, coords: c }));
        },
        () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(fallbackTimer);
          const city = weatherCityForUf(uf);
          if (city) dispatch(fetchWeather({ city }));
        },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 },
      );
      return () => window.clearTimeout(fallbackTimer);
    }
    const city = weatherCityForUf(uf);
    if (city) dispatch(fetchWeather({ city }));
  }, [dispatch, weatherCity, coords, useCurrentLocation, uf]);

  const weatherLoading = useAppSelector(selectWeatherLoading);
  const handleRefresh = async () => {
    setRefreshing(true);
    const tasks: Promise<unknown>[] = [dispatch(fetchQuotes(uf))];
    if (weatherCity) {
      tasks.push(dispatch(fetchWeather({ city: weatherCity })));
    } else if (coords) {
      tasks.push(dispatch(fetchWeather({ lat: coords.lat, lon: coords.lon })));
    } else {
      const city = weatherCityForUf(uf);
      if (city) tasks.push(dispatch(fetchWeather({ city })));
    }
    await Promise.all(tasks);
    setRefreshing(false);
  };

  const updatedAt = items[0]?.quotedAt;

  return (
    <Screen
      headerTitle="Mercado"
      headerRightIcon="icon-filter-line"
      onClickHeaderRight={() => navigate('/app/market/settings')}
      hasTabNavigation
      hasPadding={false}
      refreshLoading={refreshing}
      handleRefresh={handleRefresh}
    >
      <div className="px-small flex flex-col gap-medium pb-huge pt-medium">
        <WeatherCard weather={weather} loading={weatherLoading} />

        <div className="flex flex-col gap-smaller">
          <div className="flex items-center justify-between gap-smaller">
            <div className="min-w-0 flex-1">
              <Text className="text-paragraphLG color-complement-01">Cotações</Text>
              <Text className="text-captionMD color-intense-05">
                {`Atualizado ${relativeFromNow(updatedAt)}`}
              </Text>
            </div>
            <div className="w-[140px]">
              <StatePicker
                value={uf}
                onChange={(v) => dispatch(setManualUf(v))}
                placeholder="UF"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-smaller">
            {items.map((item) => (
              <QuoteRow
                key={item.product}
                item={item}
                onClick={() => navigate(`/app/market/${item.product}`)}
              />
            ))}
            {items.length === 0 ? (
              <div className="rounded-[18px] bg-intense-01 border border-intense-03 p-medium text-center">
                <Text className="text-bodyMD color-intense-06">
                  Não foi possível carregar as cotações agora.
                </Text>
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-[18px] bg-intense-01 border border-intense-03 p-small shadow-level1">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-paragraphLG color-complement-01">Juros de referência</Text>
              <Text className="text-captionMD color-intense-05">
                Banco Central · séries anualizadas base 252
              </Text>
            </div>
            <Text className="text-captionMD color-intense-05">Taxas oficiais</Text>
          </div>
          <div className="mt-small grid grid-cols-2 gap-small">
            {(['cdi', 'selic'] as const).map((indexer) => {
              const item = marketIndexes.find((rate) => rate.indexer === indexer);
              return (
                <div key={indexer} className="rounded-[14px] bg-intense-02 px-small py-smaller">
                  <Text className="text-captionLG color-intense-06 uppercase">{indexer}</Text>
                  <Text className="mt-quark text-paragraphLG color-complement-01">
                    {marketIndexesLoading ? 'Carregando…' : item ? `${(item.value * 100).toFixed(4)}% a.a.` : '—'}
                  </Text>
                  <Text className="mt-quark text-captionMD color-intense-05">
                    {item ? `Referência ${item.date}` : marketIndexesError ? 'Falha ao consultar' : 'Indisponível'}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>

        <Text className="text-captionMD color-intense-05 text-center" as="p">
          Soja e milho são indicadores nacionais CEPEA/ESALQ. Boi gordo varia por
          estado quando disponível.
        </Text>
      </div>
    </Screen>
  );
}
